<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  steps?: number;
  step?: number;
  fromStep?: number;
  theme?: string;
  stepTextOn?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  steps: 0,
  step: 0,
  fromStep: 0,
  theme: 'light',
  stepTextOn: false,
});

const { t, locale } = useI18n();

const green = ref('0%');
const light = ref('0%');
const widthStep = computed(() => 100 / props.steps);

const stepsLeft = computed(() => {
  return props.steps - props.step - 1;
});

const stepsLeftString = computed(() => {
  const preMarker = locale.value === 'ar' ? '\u200F' : '';
  return `${preMarker}${stepsLeft.value}`;
});

function updateProgressBar() {
  green.value = `${Math.trunc(widthStep.value * props.fromStep)}%`;
  light.value = `${Math.trunc(widthStep.value * (props.fromStep + 1))}%`;
  setTimeout(() => {
    light.value = `${Math.trunc(widthStep.value * (props.step + 1))}%`;
    green.value = `${Math.trunc(widthStep.value * props.step)}%`;
  }, 10);
}

watch(
  () => props.step,
  () => {
    updateProgressBar();
  },
);

onMounted(() => {
  updateProgressBar();
});
</script>

<template>
  <div class="progress">
    <div class="scale-progress">
      <div class="light" :style="{ width: light }" />
      <div class="green" :style="{ width: green }" />
    </div>
    <div v-if="stepTextOn" class="progress_text">
      {{ t('qic.steps', { steps: stepsLeftString }) }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@styles/utils' as *;

.progress {
  max-width: $flow-width;
  margin-inline: auto;
  margin-bottom: 32px;
  display: flex;

  &_text {
    font-style: normal;

    @include caption-regular;

    text-align: center;
    margin-top: 8px;
  }
}

.scale-progress {
  background: var(--colorBgSrfTertiaryAlpha);
  width: 100%;
  height: 8px;
  border-radius: 100px;
  overflow: hidden;
  position: relative;
  display: flex;

  .light {
    border-radius: 50px;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    font-size: 0;
    transition: 1s;
    background: #9bf1ad;
    display: flex;
  }

  .green {
    border-radius: 50px;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    font-size: 0;
    transition: 1s;
    background: #57c863;
    display: flex;
  }
}

.rtl {
  .scale-progress {
    transform: scale(-1, 1);
  }
}
</style>
